import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  MenuItem,
  Switch,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../../DataLayouts/Header";
import toast from "react-hot-toast";
import { fetchWorkModel } from "../../../Apis/Workshift-api";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import { MAIN_URL } from "../../../Configurations/Urls";
import axios from "axios";

const WEEK_DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const defaultDaysData = () =>
  WEEK_DAYS.map((day) => ({
    day,
    is_working_day: ["Saturday", "Sunday"].includes(day) ? 0 : 1,
  }));

function toWorkingFlag(value) {
  return value === true || value === 1 || value === "1" ? 1 : 0;
}

function normalizeWorkModels(payload) {
  if (Array.isArray(payload?.workmodel?.data)) return payload.workmodel.data;
  if (Array.isArray(payload?.workmodel)) return payload.workmodel;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
}

function WorkModelDayForm({ mode }) {
  const { id } = useParams();
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const isEditOrView = mode === "edit" || mode === "view";
  const workModelIdFromRoute = id != null && String(id).match(/^\d+$/) ? String(id) : "";

  const [formData, setFormData] = useState({
    organization_work_model_id: workModelIdFromRoute,
    remarks: "",
  });

  const [daysData, setDaysData] = useState(defaultDaysData);
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(isEditOrView);
  const [btnLoading, setbtnLoading] = useState(false);
  const [workModels, setWorkModels] = useState([]);
  const submittingRef = useRef(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (!org?.organization_id) return;
    fetchWorkModel(org.organization_id)
      .then((data) => {
        setWorkModels(normalizeWorkModels(data));
      })
      .catch((err) => {
        toast.error("Failed to fetch work models");
        console.error(err);
      });
  }, [org?.organization_id]);

  useEffect(() => {
    if (!isEditOrView || !id || !org?.organization_id) return;

    if (!/^\d+$/.test(String(id))) {
      toast.error("Invalid work model id");
      setLoading(false);
      return;
    }

    let cancelled = false;

    const getDataById = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${MAIN_URL}/api/organizations/${org.organization_id}/workmodel-days/${encodeURIComponent(id)}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (cancelled) return;

        const workshift = response.data?.workshift;
        if (!workshift) {
          toast.error("Work model days not found");
          return;
        }

        const modelId = String(
          workshift.organization_work_model_id ?? id ?? ""
        );

        setFormData({
          organization_work_model_id: modelId,
          remarks: workshift.remarks || "",
        });

        const backendDays = Array.isArray(workshift.days) ? workshift.days : [];
        setDaysData(
          WEEK_DAYS.map((dayName) => {
            const backendDay = backendDays.find(
              (d) =>
                String(d.day_of_week || "").toLowerCase() ===
                dayName.toLowerCase()
            );
            return {
              day: dayName,
              is_working_day: backendDay
                ? toWorkingFlag(backendDay.is_working_day)
                : 0,
            };
          })
        );
      } catch (err) {
        if (cancelled) return;
        console.error("Error fetching data:", err);
        const errors = err.response?.data?.errors;
        const msg =
          errors?.organization_work_model_id?.[0] ||
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to fetch work model days";
        toast.error(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    getDataById();
    return () => {
      cancelled = true;
    };
  }, [mode, id, org?.organization_id, isEditOrView]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleDayToggle = (index) => {
    setDaysData((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        is_working_day: updated[index].is_working_day === 1 ? 0 : 1,
      };
      return updated;
    });
  };

  const validateForm = () => {
    const errors = {};
    const modelId = Number(formData.organization_work_model_id);
    if (!Number.isInteger(modelId) || modelId <= 0) {
      errors.organization_work_model_id = "Work Model is required.";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (submittingRef.current || btnLoading || mode === "view") return;
    if (!validateForm()) return;

    const modelId = Number(
      isEditOrView && workModelIdFromRoute
        ? workModelIdFromRoute
        : formData.organization_work_model_id
    );

    submittingRef.current = true;
    setbtnLoading(true);
    try {
      const payload = {
        organization_work_model_id: modelId,
        remarks: formData.remarks || "",
        days: daysData.map((dayData) => ({
          day_of_week: dayData.day,
          is_working_day: dayData.is_working_day === 1,
        })),
      };

      const url =
        mode === "edit"
          ? `${MAIN_URL}/api/organizations/${org?.organization_id}/workmodel-days/${encodeURIComponent(modelId)}`
          : `${MAIN_URL}/api/organizations/${org?.organization_id}/workmodel-days`;

      const method = mode === "edit" ? "put" : "post";

      await axios[method](url, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });

      toast.success(
        mode === "edit"
          ? "Work Model days updated successfully!"
          : "Work Model days created successfully!"
      );
      navigate(-1);
    } catch (err) {
      console.error("Error details:", err);
      if (err.response?.status === 422) {
        const validationErrors = err.response.data.errors || {};
        const flat = {};
        Object.keys(validationErrors).forEach((key) => {
          flat[key] = Array.isArray(validationErrors[key])
            ? validationErrors[key][0]
            : validationErrors[key];
        });
        setFormErrors(flat);
        Object.values(flat).forEach((errorMsg) => {
          if (errorMsg) toast.error(errorMsg);
        });
      } else if (err.response?.status === 401) {
        toast.error("Unauthorized access");
      } else if (err.response?.status === 404) {
        toast.error("Work Model days not found");
      } else {
        toast.error(err.response?.data?.error || "Something went wrong");
      }
    } finally {
      submittingRef.current = false;
      setbtnLoading(false);
    }
  };

  return (
    
      <Header
        mode={mode}
        updateMessage={"Work Model Days"}
        addMessage={"Work Model Days"}
        homeLink={"/organization/work-model-days"}
        homeText={"Work Model Days"}
        onSubmit={handleSubmit}
        loading={loading}
        btnLoading={btnLoading}>
      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper elevation={4} sx={{ p: 3 }}>
          <Grid container spacing={5}>
            <TextField
              select
              fullWidth
              label="Work Model"
              name="organization_work_model_id"
              value={String(formData.organization_work_model_id || "")}
              onChange={handleChange}
              error={!!formErrors.organization_work_model_id}
              helperText={
                Array.isArray(formErrors.organization_work_model_id)
                  ? formErrors.organization_work_model_id[0]
                  : formErrors.organization_work_model_id
              }
              required
              disabled={
                workModels.length === 0 || mode === "edit" || mode === "view"
              }
            >
              {workModels.length === 0 ? (
                <MenuItem disabled value="">
                  No work models available
                </MenuItem>
              ) : (
                workModels.map((option) => (
                  <MenuItem
                    key={option.organization_work_model_id}
                    value={String(option.organization_work_model_id)}
                  >
                    {option.work_model_name}
                  </MenuItem>
                ))
              )}
            </TextField>

            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 1, ml: 6, fontWeight: 600 }}>
                Working Days
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  width: "fit-content",
                  backgroundColor: "#f5f5f5",
                  p: 2,
                  ml: 5,
                  borderRadius: 2,
                  boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
                }}
              >
                {daysData.map((dayData, index) => (
                  <Box
                    key={dayData.day}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      minWidth: 200,
                      backgroundColor: "#fff",
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 500, mr: 4 }}>
                      {dayData.day}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color:
                            dayData.is_working_day === 1
                              ? "success.main"
                              : "text.secondary",
                          fontWeight: 500,
                        }}
                      >
                        {dayData.is_working_day === 1 ? "Working" : "Off"}
                      </Typography>
                      <Switch
                        disabled={mode === "view"}
                        checked={dayData.is_working_day === 1}
                        onChange={() => handleDayToggle(index)}
                        color="primary"
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>

            <TextField
              fullWidth
              label="Remarks"
              name="remarks"
              disabled={mode === "view"}
              value={formData.remarks}
              onChange={handleChange}
              error={!!formErrors.remarks}
              helperText={formErrors.remarks}
              inputProps={{ maxLength: 1000 }}
              multiline
              minRows={3}
              maxRows={5}
            />
          </Grid>

          <Grid container spacing={2} mt={2}>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                size="medium"
                onClick={handleSubmit}
                disabled={loading || btnLoading || mode === "view"}
                sx={{
                  borderRadius: 2,
                  minWidth: 120,
                  textTransform: "capitalize",
                  fontWeight: 500,
                }}
              >
                {loading || btnLoading ? (
                  <CircularProgress size={22} sx={{ color: "#fff" }} />
                ) : mode === "edit" ? (
                  "Update"
                ) : (
                  "Submit"
                )}
              </Button>
            </Grid>

            <Grid item>
              <Button
                variant="outlined"
                color="primary"
                size="medium"
                onClick={() => navigate(-1)}
                disabled={btnLoading}
                sx={{
                  borderRadius: 2,
                  minWidth: 120,
                  textTransform: "capitalize",
                  fontWeight: 500,
                }}
              >
                Cancel
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}
    </Header>
  );
}

export default WorkModelDayForm;
